import {mapToObj, omit, isNonNullish, pipe} from 'remeda'
import {match} from 'ts-pattern'
import {z, ZodObject, ZodRawShape, type ZodSchema} from 'zod'
import {compactObj} from './collection'
import {notNull, parseOrErr, zx, type JsonObject} from './type'
import {err, ok, type Err, type OK, type Res} from './promise'
import {queryStringSerializer} from './queryStringSerializer'

export const searchParamsToObj = (params: URLSearchParams) => mapToObj(Array.from(params), p => p)

export const parseURLQuery = <T extends ZodRawShape>(urlRaw: string, schema: ZodObject<T>) =>
  schema.parse(searchParamsToObj(new URL(urlRaw).searchParams))

export interface UriParts {
  uri?: string
  protocol?: string
  host?: string
  port?: number
  username?: string
  password?: string
  path?: string[]
  query?: {[key: string]: string}
  hash?: string
}

type QParsed<Q> = Q extends ZodRawShape ? ReturnType<ZodObject<Q>['parse']> : {[key: string]: string}

/** fluent wrapper over URL */
export class Uri<Q extends ZodRawShape = {}> {
  parts: UriParts
  schema?: ZodObject<Q>

  constructor(uri: string | UriParts, schema?: ZodObject<Q>) {
    this.parts = typeof uri === 'string' ? Uri.parse(uri) : uri
    this.schema = schema
  }

  static parse = (url: string): UriParts => {
    // test if it is relative by matching on the protocol syntax with regex
    const isAbsolute = /^[a-zA-Z0-9+.-]+:\/\//.test(url)
    const u = new URL(url, 'http://_') // use a bae in case the url is relative

    return {
      ...(isAbsolute ?
        {
          protocol: u.protocol,
          host: u.host,
          port: u.port ? Number.parseInt(u.port) : undefined,
          username: u.username,
          password: u.password
        }
      : undefined),
      // remove the first slash, split by slashes and consider the trailing slash an emtpy token
      path: u.pathname?.split('/').filter((s, i) => isNonNullish(s) && (i != 0 || s != '')),
      query: searchParamsToObj(u.searchParams),
      hash: u.hash
    }
  }

  static asUrl = (p: UriParts): URL => {
    const u = new URL(`${p.protocol ?? 'http:'}//${p.host ?? '_'}`)
    if (p.port) u.port = p.port.toString()
    if (p.username) u.username = p.username
    if (p.password) u.password = p.password
    if (p.path?.length) {
      const lastIsSlash = p.path[p.path.length - 1] == ''
      if (lastIsSlash) p.path.pop()
      u.pathname = p.path.join('/') + (lastIsSlash ? '/' : '')
    }
    if (p.hash) u.hash = p.hash
    if (p.query) for (const [key, value] of Object.entries(p.query)) u.searchParams.append(key, value)
    return u
  }

  static format = (p: UriParts) => {
    const url = Uri.asUrl(p).toString()
    return p.host == null || p.protocol == null ? url.replace(/^http:\/\/_/, '') : url
  }

  with = (parts: Partial<UriParts>) => new Uri({...this.parts, ...parts}, this.schema)

  /** appends the given query string values to the existing, returns the new Uri */
  mergeQuery = (q: Q extends ZodRawShape ? Partial<z.infer<ZodObject<Q>>> : {[key: string]: string | undefined}) => {
    if (this.schema) {
      const serializer = queryStringSerializer(this.schema)
      const serialized = serializer.serializeToObject(q as Partial<z.infer<ZodObject<Q>>>)
      return this.with({query: {...this.parts.query, ...compactObj(serialized)}})
    }
    return this.with({query: {...this.parts.query, ...compactObj(q as {[key: string]: string | undefined})}})
  }

  /** appends a path part to the existing one. returns a new Uri */
  addPath = (...path: (string | undefined)[]) =>
    this.with({path: (this.parts.path ?? []).concat(path.filter(isNonNullish))})

  get domainAndPath() {
    return [this.host, ...(this.path ?? [])].filter(notNull).join('/')
  }

  /** a full string representation of the url */
  get url() {
    return Uri.format(this.parts)
  }
  get urlSansQuery() {
    return Uri.format(omit(this.parts, ['hash', 'query']))
  }
  get urlSansTrailingSlash() {
    return this.pathHasTrailingSlash ? this.with({path: this.path!.slice(0, -1)}).url : this.url
  }
  get urlSanitized() {
    return Uri.format(this.with({password: this.parts.password ? '***' : undefined}).parts)
  }
  /** url without the path hash or query parts */
  get root() {
    return Uri.format(omit(this.parts, ['hash', 'query', 'path']))
  }
  /** the host part of the url. Just like new Url().host */
  get host() {
    return this.parts.host
  }
  get hostSansPort() {
    return this.parts.host?.split(':')[0]
  }
  get path() {
    return this.parts.path
  }
  get pathHasTrailingSlash() {
    return this.parts.path?.[this.parts.path.length - 1] == ''
  }
  get protocol() {
    return this.parts.protocol
  }
  get username() {
    return this.parts.username
  }
  get password() {
    return this.parts.password
  }
  get port() {
    return this.parts.port
  }
  toString() {
    return Uri.format(this.parts)
  }

  /**
   * Parses the url query string using the given schema.
   * If no schema is provided, it returns the raw query parts.
   */
  query = (): QParsed<Q> =>
    this.schema ?
      match(this.schema.safeParse(this.parts.query))
        .with({success: true}, d => d.data as QParsed<Q>)
        .otherwise(() => ({}) as QParsed<Q>)
    : ((this.parts.query ?? {}) as QParsed<Q>)

  /** plain fetch, but fluent */
  fetch = (init?: RequestInit) => fetch(this.url, init)

  getJson = <Z extends ZodSchema<any>, E extends ZodSchema<{message: string}>>(
    schema: Z,
    init?: RequestInit & {errorSchema?: E}
  ) =>
    this.fetch({
      ...init,
      headers: {Accept: 'application/json', ...init?.headers}
    }).then(r => processFetchJsonResponse(r, schema, init?.errorSchema))

  /** post a json body */
  postJson = <Z extends ZodSchema<ZodRawShape>, E extends ZodSchema<{message: string}>>(
    body: JsonObject,
    initRaw?: RequestInit & {
      /** the returning schema, if empty defaults to assuming no result */
      schema?: Z
      /** the error schema, if empty defaults to {message: string} */
      errorSchema?: E
    }
  ) => {
    const {schema, errorSchema, ...init} = initRaw ?? {}
    return this.fetch({
      ...init,
      method: 'POST',
      body: JSON.stringify(body),
      headers: {'Content-Type': 'application/json', Accept: 'application/json', ...init?.headers}
    }).then(r => processFetchJsonResponse(r, schema, errorSchema))
  }
}

export const processFetchJsonResponse = async <
  Z extends ZodSchema | undefined,
  E extends ZodSchema<{message: string}> | undefined
>(
  response: Response,
  schema?: Z,
  errorSchema?: E
): Promise<
  Z extends undefined ?
    E extends undefined ?
      Res<{}, {message: string}>
    : Res<{}, z.infer<NonNullable<E>>>
  : Res<z.infer<NonNullable<Z>>, E extends undefined ? {message: string} : z.infer<NonNullable<E>>>
> => {
  const responseJson = await response.json().catch(e => undefined)
  if (response.ok) {
    if (!schema) return {ok: true, ...responseJson} as any
    const parseRes = schema.safeParse(responseJson)
    if (!parseRes.success) return err(`error parsing api response: ${zx.fmtError(parseRes.error)}`) as any
    return {ok: true, ...parseRes.data} as any
  }
  if (!errorSchema)
    return {ok: false, message: `Server responded with error (${response.status})`, ...responseJson} as any
  const parseRes = errorSchema.safeParse(responseJson)
  if (!parseRes.success) return err(`error parsing api response: ${zx.fmtError(parseRes.error)}`) as any
  return {ok: false, ...parseRes.data} as any
}

/** creates a Uri object for fluently building/modifying a url */
export const uri = <Q extends ZodRawShape = {}>(uri: string | UriParts, schema?: ZodObject<Q>) =>
  new Uri<Q>(uri, schema)
